/* vitae, bulb emulator — root-scoped factory (multi-instance)
   Same physics/behaviour as the single-instance version, but every DOM hook is
   resolved WITHIN a passed-in root element, so any number of emulators can run
   on one page. Element hooks (inside root):
     [data-emu="switch|bulb|glow|mode|modeName|hint|bars|timer"]
     .glass / .fil (in the bulb svg), .tnum / .tlab (in timer)
     [data-val="k|cri|lm|w|mder|use|usenote"]
     .emu-mbtn[data-model] (+ optional .emu-mthumb)
   Call window.initVitaeEmulator(rootEl). */
(function () {
  "use strict";

  var WINDOW = 11000; // ms, the off/on window to advance a mode

  //          420   440   460   480   500   520   540   560   580   600   620   640   660   680   700   720   740   760   780
  var SPD = {
    k1200: [0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00, -0.85, 0.80, 0.50, 1.00, 0.55, 0.15, 0.04, 0.01, 0.00, 0.00, 0.00, 0.00],
    k1600: [0.00, 0.01, 0.03, 0.04, 0.05, 0.08, 0.11, 0.15, 0.21, 0.30, 0.40, 0.55, 0.66, 0.56, 0.73, 0.70, 1.00, 0.25, 0.05],
    k1800: [0.00, 0.02, 0.05, 0.05, 0.07, 0.11, 0.18, 0.23, 0.34, 0.54, 0.78, 0.96, 0.84, 0.56, 0.52, 1.00, 0.45, 0.10, 0.02],
    k2000: [0.02, 0.05, 0.05, 0.06, 0.10, 0.15, 0.24, 0.36, 0.52, 0.70, 0.85, 1.00, 0.95, 0.86, 0.74, 0.60, 0.46, 0.30, 0.13],
    k2700: [0.11, 0.17, 0.10, 0.14, 0.25, 0.34, 0.44, 0.52, 0.62, 0.77, 0.92, 1.00, 0.93, 0.80, 0.58, 0.45, 0.33, 0.20, 0.10],
    k4000: [0.30, 0.72, 0.80, 0.45, 0.61, 0.69, 0.71, 0.72, 0.78, 0.89, 0.97, 1.00, 0.93, 0.80, 0.56, 0.43, 0.30, 0.18, 0.08]
  };
  // maps a mode's Kelvin value to the baked-light cutout image key (assets/cct-<key>.png)
  var CCT_IMG = { 1200: "1200", 1600: "1600and1800", 1800: "1600and1800", 2000: "2000", 2700: "2700", 4000: "4000" };
  function cctKey(k) { return CCT_IMG[k] || "2700"; }

  var BAND_COLORS = [
    "#5512b5", "#3a1fd6", "#2454ff", "#0a8cff", "#00c2d6", "#15cf57", "#5fd62b", "#a6d61f", "#ffd21f", "#ffa61a",
    "#ff6e1a", "#ff3b2f", "#f5261e", "#df1e1e", "#c41e1e", "#ab1a1a", "#8f1818", "#7a1414", "#681010"
  ];

  var MODELS = {
    office: { name: "Home Office", modes: [
      { label: "vitae orange", k: 1200, cri: "—",   w: 2, lm: 10,  mder: 0.015, hex: "#ff5a12", spd: "k1200", use: "Falling asleep", useNote: "reading yourself to sleep" },
      { label: "2700 K",       k: 2700, cri: "96+", w: 6, lm: 300, mder: 0.440, hex: "#ffb46e", spd: "k2700", use: "Early evenings", useNote: "dinner and unwinding" },
      { label: "4000 K",       k: 4000, cri: "96+", w: 7, lm: 440, mder: 0.730, hex: "#fff2e2", spd: "k4000", use: "Work \u0026 study", useNote: "focus and daylight hours" }
    ]},
    living: { name: "Living Room", modes: [
      { label: "vitae orange", k: 1200, cri: "—",   w: 2, lm: 13,  mder: 0.015, hex: "#ff5a12", spd: "k1200", use: "Falling asleep", useNote: "reading yourself to sleep" },
      { label: "2000 K",       k: 2000, cri: "96+", w: 5, lm: 250, mder: 0.330, hex: "#ff9c45", spd: "k2000", use: "Cosy evenings", useNote: "lamp-lit nights in" },
      { label: "2700 K",       k: 2700, cri: "98+", w: 6, lm: 350, mder: 0.440, hex: "#ffb46e", spd: "k2700", use: "Early evenings", useNote: "dinner and unwinding" }
    ]},
    bedroom: { name: "Bedroom", modes: [
      { label: "vitae orange", k: 1200, cri: "—",   w: 2, lm: 13,  mder: 0.015, hex: "#ff5a12", spd: "k1200", use: "Falling asleep", useNote: "reading yourself to sleep" },
      { label: "1800 K",       k: 1800, cri: "95+", w: 4, lm: 140, mder: 0.240, hex: "#ff8531", spd: "k1800", use: "Bedtime stories", useNote: "winding little ones down" },
      { label: "2000 K",       k: 2000, cri: "96+", w: 5, lm: 260, mder: 0.330, hex: "#ff9c45", spd: "k2000", use: "Cosy evenings", useNote: "lamp-lit nights in" }
    ]},
    bedroom2: { name: "Bedroom 2.0", modes: [
      { label: "vitae orange", k: 1200, cri: "—",   w: 2, lm: 14,  mder: 0.015, hex: "#ff5a12", spd: "k1200", use: "Falling asleep", useNote: "reading yourself to sleep" },
      { label: "1600 K",       k: 1600, cri: "94+", w: 4, lm: 100, mder: 0.200, hex: "#ff7620", spd: "k1600", use: "Bedtime stories", useNote: "before the kids nod off" },
      { label: "2000 K",       k: 2000, cri: "96+", w: 5, lm: 260, mder: 0.330, hex: "#ff9c45", spd: "k2000", use: "Cosy evenings", useNote: "lamp-lit nights in" }
    ]}
  };

  // ── pure helpers, computed once and shared across instances ──
  function crU(p0, p1, p2, p3, u) {
    var u2 = u * u, u3 = u2 * u;
    return Math.max(0, 0.5 * (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2 + (-p0 + 3 * p1 - 3 * p2 + p3) * u3));
  }
  var STEP = 0.5;
  function resample(a) {
    var out = [], n = a.length, last = n - 1;
    for (var t = 0; t <= last + 1e-9; t += STEP) {
      var i = Math.min(last - 1, Math.floor(t)), u = t - i;
      var v = crU(a[Math.max(0, i - 1)], a[i], a[i + 1], a[Math.min(last, i + 2)], u);
      var hi = Math.max(0, a[i], a[i + 1]);
      out.push(Math.min(hi, v));
    }
    return out;
  }
  function hex2rgb(h) { return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]; }
  function rgb2hex(r) { return "#" + r.map(function (v) { return ("0" + Math.round(v).toString(16)).slice(-2); }).join(""); }
  function colorAt(t) {
    var last = BAND_COLORS.length - 1, i = Math.min(last - 1, Math.floor(t)), u = t - i;
    var a = hex2rgb(BAND_COLORS[i]), b = hex2rgb(BAND_COLORS[i + 1]);
    return rgb2hex([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u]);
  }
  var COLORS5 = [];
  for (var t5 = 0; t5 <= (BAND_COLORS.length - 1) + 1e-9; t5 += STEP) COLORS5.push(colorAt(t5));
  var SPD10 = {};
  Object.keys(SPD).forEach(function (k) { SPD10[k] = resample(SPD[k]); });

  // ── per-instance factory ──
  window.initVitaeEmulator = function (root) {
    if (!root || root.dataset.emuInit) return;
    root.dataset.emuInit = "1";
    var $ = function (s) { return root.querySelector(s); };

    var sw       = $('[data-emu="switch"]');
    var glow     = $('[data-emu="glow"]');
    var bulb     = $('[data-emu="bulb"]');
    var glass    = $(".glass");
    var fil      = $(".fil");
    var modeEl   = $('[data-emu="mode"]');
    var modeName = $('[data-emu="modeName"]');
    var hint     = $('[data-emu="hint"]');
    var bars     = $('[data-emu="bars"]');
    var timer    = $('[data-emu="timer"]');
    var tnum     = timer && timer.querySelector(".tnum");
    var tlab     = timer && timer.querySelector(".tlab");
    var vals = {
      k: $('[data-val="k"]'), cri: $('[data-val="cri"]'), lm: $('[data-val="lm"]'),
      w: $('[data-val="w"]'), mder: $('[data-val="mder"]'),
      use: $('[data-val="use"]'), usenote: $('[data-val="usenote"]')
    };
    var mbtns = [].slice.call(root.querySelectorAll(".emu-mbtn"));
    var thumb = $(".emu-mthumb");
    var steps = $('[data-emu="steps"]');

    if (!sw || !bars || !modeEl) return; // minimum viable

    function placeThumb(btn) {
      if (!thumb || !btn) return;
      thumb.style.width = btn.offsetWidth + "px";
      thumb.style.height = btn.offsetHeight + "px";
      thumb.style.transform = "translate(" + btn.offsetLeft + "px," + btn.offsetTop + "px)";
    }
    function syncThumb() { placeThumb(root.querySelector(".emu-mbtn.on")); }

    // spectrum bars
    var barEls = [];
    COLORS5.forEach(function (c) {
      var b = document.createElement("div");
      b.className = "emu-bar";
      b.style.background = c;
      bars.appendChild(b);
      barEls.push(b);
    });

    var MID = 1, modelKey = "office", on = false, modeIndex = 0, hasBeenOn = false;
    var offAt = 0, onAt = 0, lastOnDur = 0;
    var timerRAF = 0, tweenRAF = 0, onRAF = 0;
    var disp = { k: 0, lm: 0, w: 0, mder: 0 };

    function modes() { return MODELS[modelKey].modes; }
    function cur() { return modes()[modeIndex]; }

    // step 1/2/3 indicator — one pill per mode of the active model
    function buildSteps() {
      if (!steps) return;
      steps.innerHTML = "";
      modes().forEach(function (m, i) {
        var d = document.createElement("div");
        d.className = "emu-step";
        d.innerHTML = '<span class="n">' + (i + 1) + '</span><span class="t">' + m.label + '</span>';
        steps.appendChild(d);
      });
    }
    function renderSteps() {
      if (!steps) return;
      [].slice.call(steps.children).forEach(function (el, i) {
        el.classList.toggle("on", on && i === modeIndex);
      });
    }

    function fmt(metric, v) {
      if (metric === "k")  return Math.round(v) + "";
      if (metric === "lm") return Math.round(v) + "";
      if (metric === "w")  { var r = Math.round(v * 10) / 10; return (r % 1 === 0) ? String(Math.round(r)) : r.toFixed(1); }
      return v >= 0.1 ? v.toFixed(2) : v.toFixed(3);
    }
    var UNIT = { k: "K", lm: "lm", w: "W", mder: "" };
    function setVal(metric, v) {
      if (!vals[metric]) return;
      var u = UNIT[metric];
      vals[metric].innerHTML = fmt(metric, v) + (u ? ' <small>' + u + '</small>' : '');
    }
    function tweenTo(t) {
      cancelAnimationFrame(tweenRAF);
      var start = { k: disp.k, lm: disp.lm, w: disp.w, mder: disp.mder };
      var t0 = performance.now(), dur = 520;
      (function step(now) {
        var p = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - p, 3);
        ["k", "lm", "w", "mder"].forEach(function (m) { disp[m] = start[m] + (t[m] - start[m]) * e; setVal(m, disp[m]); });
        if (p < 1) tweenRAF = requestAnimationFrame(step);
      })(t0);
    }

    function renderLight() {
      var m = cur();
      if (on) {
        modeEl.classList.add("lit");
        modeEl.style.setProperty("--mode-c", m.hex);
        if (modeName) modeName.textContent = m.label;
        if (glow) {
          glow.style.setProperty("--c", m.hex);
          glow.style.opacity = String(0.35 + 0.6 * Math.min(1, m.lm / 850));
          glow.style.transform = "translate(-50%,-50%) scale(" + (0.62 + 0.5 * Math.min(1, m.lm / 850)) + ")";
        }
        if (glass) glass.style.fill = m.hex;
        if (bulb) { bulb.classList.add("lit"); bulb.style.setProperty("--glass-c", m.hex); bulb.dataset.cct = cctKey(m.k); }
        if (fil) { fil.style.stroke = "#fff7ee"; fil.style.filter = "drop-shadow(0 0 6px " + m.hex + ")"; }
        tweenTo({ k: m.k, lm: m.lm, w: m.w, mder: m.mder });
        if (vals.cri) vals.cri.innerHTML = (m.cri && m.cri !== "—") ? m.cri : '<small style="font-size:.7em">—</small>';
        if (vals.use) vals.use.textContent = m.use || "—";
        if (vals.usenote) vals.usenote.textContent = m.useNote || "";
        var spd = SPD10[m.spd];
        barEls.forEach(function (b, i) { b.style.height = (spd[i] <= 0.002 ? 0 : spd[i] * 100) + "%"; b.style.opacity = ".95"; });
      } else {
        modeEl.classList.remove("lit");
        if (modeName) modeName.textContent = "Off";
        if (glow) glow.style.opacity = "0";
        if (glass) glass.style.fill = "#242426";
        if (bulb) { bulb.classList.remove("lit"); bulb.style.setProperty("--glass-c", "#d5d2cd"); bulb.dataset.cct = "off"; }
        if (fil) { fil.style.stroke = "#4a4a4a"; fil.style.filter = "none"; }
        ["k", "cri", "lm", "w", "mder", "use"].forEach(function (mm) { if (vals[mm]) vals[mm].innerHTML = '<small style="font-size:.7em">—</small>'; });
        if (vals.usenote) vals.usenote.textContent = "";
        ["k", "lm", "w", "mder"].forEach(function (mm) { disp[mm] = 0; });
        barEls.forEach(function (b) { b.style.height = "0%"; b.style.opacity = ".5"; });
      }
      renderSteps();
      sw.setAttribute("data-on", on ? "true" : "false");
    }

    function nextLabel() { return modes()[(modeIndex + 1) % modes().length].label; }
    function offTarget() {
      if (modeIndex === MID && lastOnDur > WINDOW) return "orange";
      return modes()[(modeIndex + 1) % modes().length].label;
    }
    function renderHint(state, secs) {
      if (!hint) return;
      var name = MODELS[modelKey].name, label = cur().label;
      if (state === "on") {
        if (modeIndex === modes().length - 1)
          hint.innerHTML = "On, <b>" + name + " · " + label + "</b>. Brightest mode, the next click cycles back to <b>orange</b>.";
        else
          hint.innerHTML = "On, <b>" + name + " · " + label + "</b>. Off and back on within 11 s to step up to <b>" + nextLabel() + "</b>.";
      } else if (state === "onstep") {
        hint.innerHTML = "<b>" + secs + " s to step up.</b> Click off and on now to reach <b>" + nextLabel() + "</b>, let it run out and the bulb settles into the evening, so the next click returns to <b>orange</b>.";
      } else if (state === "settled") {
        hint.innerHTML = "Settled into the evening, no jump to daylight from here. The next click returns to <b>orange</b>.";
      } else if (state === "window") {
        hint.innerHTML = "Quick, flip it back on within <b>" + secs + " s</b> to reach <b>" + offTarget() + "</b>.";
      } else if (state === "restart") {
        hint.innerHTML = "Cycle complete — the next click starts over on <b>orange</b>.";
      } else if (state === "expired") {
        hint.innerHTML = "Window closed. It’ll wake up on its warmest mode, orange.";
      } else {
        hint.innerHTML = "Click the switch to turn it on. It always wakes up orange.";
      }
    }

    function startTimer() {
      if (!timer) return;
      timer.classList.add("show"); timer.classList.remove("expired");
      (function tick() {
        var remain = Math.max(0, WINDOW - (performance.now() - offAt));
        var secs = Math.ceil(remain / 1000);
        if (remain > 0) {
          if (tnum) tnum.textContent = secs + "s";
          if (tlab) tlab.textContent = "to step";
          renderHint("window", secs);
          timerRAF = requestAnimationFrame(tick);
        } else {
          timer.classList.add("expired");
          if (tnum) tnum.textContent = "reset";
          if (tlab) tlab.textContent = "wakes orange";
          renderHint("expired");
        }
      })();
    }
    function stopTimer() { cancelAnimationFrame(timerRAF); if (timer) timer.classList.remove("show", "expired"); }

    function startOnTimer() {
      if (!timer) return;
      timer.classList.add("show"); timer.classList.remove("expired");
      (function tick() {
        var remain = Math.max(0, WINDOW - (performance.now() - onAt));
        var secs = Math.ceil(remain / 1000);
        if (remain > 0) {
          if (tnum) tnum.textContent = secs + "s";
          if (tlab) tlab.textContent = "to step up";
          renderHint("onstep", secs);
          onRAF = requestAnimationFrame(tick);
        } else {
          timer.classList.add("expired");
          if (tnum) tnum.textContent = "set";
          if (tlab) tlab.textContent = "evening locked";
          renderHint("settled");
        }
      })();
    }
    function stopOnTimer() { cancelAnimationFrame(onRAF); }

    function turnOn() {
      var now = performance.now();
      var quick = hasBeenOn && (now - offAt) < WINDOW;
      if (quick) {
        if (modeIndex === MID && lastOnDur > WINDOW) modeIndex = 0;
        else modeIndex = (modeIndex + 1) % modes().length;
      } else { modeIndex = 0; }
      on = true; hasBeenOn = true; onAt = now;
      stopTimer(); stopOnTimer();
      renderLight();
      if (modeIndex === MID) startOnTimer();
      else renderHint("on");
    }
    function turnOff() {
      on = false; offAt = performance.now(); lastOnDur = offAt - onAt;
      stopOnTimer();
      renderLight();
      // no countdown after the last step — the next click simply starts over on orange
      if (modeIndex < modes().length - 1) { startTimer(); }
      else { stopTimer(); renderHint("restart"); }
    }
    function toggle() { on ? turnOff() : turnOn(); }

    function selectModel(key) {
      if (key === modelKey) return;
      modelKey = key;
      modeIndex = 0;
      buildSteps();
      mbtns.forEach(function (b) { b.classList.toggle("on", b.dataset.model === key); });
      placeThumb(root.querySelector(".emu-mbtn.on"));
      syncLabels();
      stopTimer(); stopOnTimer();
      if (on) { onAt = performance.now(); hasBeenOn = true; renderLight(); renderHint("on"); }
      else { hasBeenOn = false; renderLight(); renderHint("idle"); }
    }

    sw.addEventListener("click", toggle);
    mbtns.forEach(function (b) { b.addEventListener("click", function () { selectModel(b.dataset.model); }); });

    // external control: let a variant selector elsewhere on the page drive this
    // emulator (so it needs no selector of its own). Updates any driven labels.
    function syncLabels() {
      root.querySelectorAll("[data-emu-model-label]").forEach(function (el) { el.textContent = MODELS[modelKey].name; });
    }
    root.__emuSetModel = function (key) { if (MODELS[key]) { selectModel(key); syncLabels(); } };
    root.__emuModelName = function () { return MODELS[modelKey].name; };
    syncLabels();

    buildSteps();
    renderLight();
    renderHint("idle");

    if (thumb) {
      thumb.style.transition = "none";
      syncThumb();
      requestAnimationFrame(function () { thumb.style.transition = ""; });
      window.addEventListener("resize", syncThumb);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncThumb);
    }
  };

  // auto-init any emulator roots present at load
  function boot() {
    document.querySelectorAll("[data-emu-root]").forEach(function (r) { window.initVitaeEmulator(r); });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
